import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * 通用資源查詢/異動 hooks(搭配 helpers/dck_helper 的 resource API)。
 *
 * 取代每頁手刻的 useState({loading,error,rows}) + useEffect + fetch。
 * 回傳直接餵給 <AsyncBoundary loading={q.isLoading} error={q.error} isEmpty={...}>。
 * 異動成功後自動 invalidate 該資源的查詢 → 列表自動重取(對應審核核准後刷新)。
 *
 *   const q = useResourceList(usersApi, "users", { page, keyword });
 *   const { create, remove } = useResourceMutations(usersApi, "users");
 *   create.mutate({ name, account, email });
 */
export function useResourceList(api, key, params = {}) {
  return useQuery({
    queryKey: [key, "list", params],
    queryFn: () => api.list(params),
    placeholderData: (prev) => prev, // 換頁時保留舊資料,避免閃爍
  });
}

// —— 真後端契約版 hooks(搭配 createBackendResourceApi;ADR-0001) ——
// 分頁信封 { total_count, items[], page_index, ... },參數 pageIndex(0 起算)/pageSize。
//   const q = useBackendList(dcCustomersApi, "dcCustomers", { keyword, pageIndex, pageSize });
//   const { create, update, remove } = useBackendMutations(dcCustomersApi, "dcCustomers");
//   update.mutate(dto); // PUT /Update,body 含 id
export function useBackendList(api, key, params = {}) {
  return useQuery({
    queryKey: [key, "list", params],
    queryFn: () => api.getAll(params),
    placeholderData: (prev) => prev,
  });
}

export function useBackendItem(api, key, id) {
  return useQuery({
    queryKey: [key, "item", id],
    queryFn: () => api.getById(id),
    enabled: id !== undefined && id !== null && id !== "",
  });
}

export function useBackendMutations(api, key) {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: [key] });
  return {
    create: useMutation({ mutationFn: (data) => api.create(data), onSuccess }),
    update: useMutation({ mutationFn: (data) => api.update(data), onSuccess }),
    remove: useMutation({ mutationFn: (id) => api.remove(id), onSuccess }),
    importRows: useMutation({
      mutationFn: (rows) => api.importRows(rows),
      onSuccess,
    }),
  };
}

export function useResourceItem(api, key, id) {
  return useQuery({
    queryKey: [key, "item", id],
    queryFn: () => api.get(id),
    enabled: id !== undefined && id !== null,
  });
}

export function useResourceMutations(api, key) {
  const qc = useQueryClient();
  const onSuccess = () => qc.invalidateQueries({ queryKey: [key] });
  return {
    create: useMutation({ mutationFn: (data) => api.create(data), onSuccess }),
    update: useMutation({ mutationFn: ({ id, data }) => api.update(id, data), onSuccess }),
    remove: useMutation({ mutationFn: (id) => api.remove(id), onSuccess }),
    importRows: useMutation({ mutationFn: (rows) => api.importRows(rows), onSuccess }),
  };
}
